import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import {
  type CellClickedEvent,
  type ColDef,
  type ICellRendererParams
} from "ag-grid-community";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Pagination } from "shared/components/Pagination";
import { AgGrid } from "shared/components/AgGrid";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { AddressValidationIssue } from "api/addressValidationIssues";
import { useAddressValidationIssues } from "./useAddressValidationIssues";

const ROW_HEIGHT = 72;
const EMPTY_ISSUES: AddressValidationIssue[] = [];

const FlaggedAtCell = ({ value }: { value: string }) => {
  const date = value ? DateTime.fromISO(value) : null;
  if (!date?.isValid)
    return <span className="text-sm text-content-400">—</span>;

  return (
    <p className="text-sm text-content-1000">{date.toFormat("MMM d, yyyy")}</p>
  );
};

export const AddressValidationIssuesCard = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useAddressValidationIssues(page);

  const items = data?.items ?? EMPTY_ISSUES;
  const total = data?.total_count ?? 0;
  const totalPages = data?.page_count ?? 1;

  const onCellClicked = useCallback(
    (event: CellClickedEvent<AddressValidationIssue>) => {
      const row = event.data;
      if (!row) return;

      if (row.unit_id)
        navigate(`/properties/${row.property_id}/units/${row.unit_id}`);
      else navigate(`/properties/${row.property_id}`);
    },
    [navigate]
  );

  const columns = useMemo<ColDef<AddressValidationIssue>[]>(
    () => [
      {
        headerName: "Address",
        colId: "address",
        flex: 2,
        minWidth: 240,
        cellRenderer: (params: ICellRendererParams<AddressValidationIssue>) => {
          const issue = params.data;
          if (!issue) return null;
          return (
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-content-1000">
                {issue.property_name}
              </p>
              <Show if={Boolean(issue.address)}>
                <p className="truncate text-xs text-content-400">
                  {issue.address}
                </p>
              </Show>
            </div>
          );
        }
      },
      {
        headerName: "Unit number",
        colId: "unit_number",
        flex: 1,
        minWidth: 120,
        cellRenderer: (params: ICellRendererParams<AddressValidationIssue>) => (
          <p className="text-sm font-medium text-content-1000">
            {params.data?.unit_number ?? "—"}
          </p>
        )
      },
      {
        headerName: "Issue",
        colId: "issue",
        flex: 2,
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<AddressValidationIssue>) => (
          <Badge variant={BadgeVariant.ERROR} size={BadgeSize.SM}>
            {params.data?.issue}
          </Badge>
        )
      },
      {
        headerName: "Date first flagged",
        colId: "first_flagged_at",
        flex: 1,
        minWidth: 160,
        cellRenderer: (params: ICellRendererParams<AddressValidationIssue>) => (
          <FlaggedAtCell value={params.data?.first_flagged_at ?? ""} />
        )
      }
    ],
    []
  );

  return (
    <Card className="w-full p-6">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-bold text-content-1000">
          Address validation issues
        </h3>
        <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          {total}
        </Badge>
      </div>

      <div className="mt-2">
        <AgGrid<AddressValidationIssue>
          columns={columns}
          rows={items}
          loading={isLoading}
          rowHeight={ROW_HEIGHT}
          wrapperClassName="!rounded-none !border-0"
          themeOverrides={{ "--ag-cell-horizontal-padding": "0px" }}
          getRowId={(params) =>
            `${params.data.property_id}-${params.data.unit_id ?? "property"}`
          }
          onCellClicked={onCellClicked}
          noRowsFallback={
            isError ? (
              <NoRowsFallback
                title="Couldn't load address issues"
                text="Something went wrong loading address validation issues. Please try again."
              />
            ) : (
              <NoRowsFallback
                title="No address issues"
                text="None of your property or unit addresses currently have a validation issue."
              />
            )
          }
        />
      </div>

      <Pagination
        className="mt-6 justify-end"
        total={totalPages}
        current={page}
        onChange={setPage}
      />
    </Card>
  );
};
